"use client";

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { observarInvalidacaoCacheCliente } from "@/lib/fetch-cache-cliente";
import { contextoExperienciaValido, useExperienciaProduto } from "./ExperienciaProvider";

type UrlAvatar = { chave: string; url: string; expiraEm: number; b2: boolean };
const urlsAvatar = new Map<string, UrlAvatar>();
const ouvintesAvatar = new Set<() => void>();
let revisaoAvatar = 0;
let varianteAvatar = 0;
let sessaoAvatarBloqueada = false;
type ContextoAvatar = { id: string };
let contextoAvatar: ContextoAvatar | null = null;
const contextosRevogados = new WeakSet<ContextoAvatar>();

function contextoPermitido(contexto: ContextoAvatar | null) {
  return !!contexto && contextoAvatar === contexto && !sessaoAvatarBloqueada
    && !contextosRevogados.has(contexto) && contextoExperienciaValido(contexto);
}

// Chamado somente pelos efeitos que recebem dados validados do Provider.
function habilitarContextoAvatar(contexto: ContextoAvatar | null) {
  if (!contexto || contextosRevogados.has(contexto) || !contextoExperienciaValido(contexto)) return;
  if (contextoAvatar !== contexto) {
    if (perfilAvatar) { pararPerfil(perfilAvatar); perfilAvatar.foto = null; avisarPerfil(perfilAvatar); }
    if (sessaoAvatarBloqueada || contextoAvatar?.id !== contexto.id) urlsAvatar.clear();
    contextoAvatar = contexto;
    sessaoAvatarBloqueada = false;
  }
}

function identificarAvatar(src: string, uid: string): UrlAvatar | null {
  try {
    const url = new URL(src);
    if (!/\/perfis\/[^/]+\/avatar$/.test(url.pathname)) return null;
    const b2 = url.pathname.startsWith("/media/perfis/") && url.searchParams.has("sig");
    let expiraEm: number;
    if (b2) {
      expiraEm = Number(url.searchParams.get("exp")) * 1000;
      url.searchParams.delete("exp");
      url.searchParams.delete("sig");
      url.searchParams.delete("avatar-revisao");
    } else {
      const data = url.searchParams.get("X-Amz-Date") ?? "";
      const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(data);
      if (!match || !url.searchParams.has("X-Amz-Signature")) return null;
      expiraEm = Date.UTC(...[
        Number(match[1]), Number(match[2]) - 1, Number(match[3]),
        Number(match[4]), Number(match[5]), Number(match[6]),
      ] as [number, number, number, number, number, number])
        + Number(url.searchParams.get("X-Amz-Expires")) * 1000;
      for (const chave of [...url.searchParams.keys()]) {
        if (chave.startsWith("X-Amz-")) url.searchParams.delete(chave);
      }
    }
    if (!Number.isFinite(expiraEm) || expiraEm <= 0) return null;
    url.searchParams.sort();
    return { chave: JSON.stringify([uid, url.origin, url.pathname, url.search]), url: src, expiraEm, b2 };
  } catch { return null; }
}

function reutilizarAvatar(src: string | null, uid: string | null): string | null {
  if (sessaoAvatarBloqueada) return null;
  if (!src || !uid) return src;
  const identificada = identificarAvatar(src, uid);
  if (!identificada) return src; // URLs desconhecidas não recebem validade inventada.
  for (const [chave, entrada] of urlsAvatar) {
    if (entrada.expiraEm <= Date.now()) urlsAvatar.delete(chave);
  }
  const entrada = urlsAvatar.get(identificada.chave) ?? identificada;
  if (entrada.expiraEm <= Date.now()) return null;
  urlsAvatar.delete(entrada.chave);
  urlsAvatar.set(entrada.chave, entrada);
  while (urlsAvatar.size > 50) urlsAvatar.delete(urlsAvatar.keys().next().value!);
  if (!entrada.b2 || !varianteAvatar) return entrada.url;
  // A assinatura B2 existente cobre somente pathname + exp; nunca modificar query AWS.
  const url = new URL(entrada.url);
  url.searchParams.set("avatar-revisao", String(varianteAvatar));
  return url.href;
}

type PerfilAvatar = {
  contexto: ContextoAvatar;
  uid: string;
  foto: string | null;
  pronta: boolean;
  bloqueado: boolean;
  ouvintes: Set<(foto: string | null) => void>;
  pendente?: Promise<void>;
  abortar?: AbortController;
  timer?: ReturnType<typeof setTimeout>;
};
let perfilAvatar: PerfilAvatar | null = null;

function avisarPerfil(perfil: PerfilAvatar) {
  for (const ouvinte of perfil.ouvintes) ouvinte(contextoPermitido(perfil.contexto) ? perfil.foto : null);
}

function pararPerfil(perfil: PerfilAvatar) {
  clearTimeout(perfil.timer);
  perfil.timer = undefined;
  perfil.abortar?.abort();
  perfil.abortar = undefined;
  perfil.pendente = undefined;
}

function agendarPerfil(perfil: PerfilAvatar) {
  clearTimeout(perfil.timer);
  perfil.timer = undefined;
  const expiraEm = perfil.foto && identificarAvatar(perfil.foto, perfil.uid)?.expiraEm;
  if (!expiraEm || !perfil.ouvintes.size || perfil.bloqueado || !contextoPermitido(perfil.contexto)) return;
  perfil.timer = setTimeout(() => {
    perfil.pronta = false;
    void carregarPerfil(perfil);
  }, Math.min(2_147_483_647, Math.max(0, expiraEm - Date.now())));
}

function carregarPerfil(perfil: PerfilAvatar): Promise<void> {
  if (perfil.bloqueado || !contextoPermitido(perfil.contexto)) return Promise.resolve();
  if (perfil.pendente) return perfil.pendente;
  const expiraEm = perfil.foto && identificarAvatar(perfil.foto, perfil.uid)?.expiraEm;
  if (perfil.pronta && (!expiraEm || expiraEm > Date.now())) {
    avisarPerfil(perfil);
    agendarPerfil(perfil);
    return Promise.resolve();
  }
  const abortar = new AbortController();
  perfil.abortar = abortar;
  const atual = () => perfilAvatar === perfil && contextoPermitido(perfil.contexto)
    && perfil.abortar === abortar && !abortar.signal.aborted;
  perfil.pendente = fetch("/api/perfil", { credentials: "include", signal: abortar.signal })
    .then(async (resposta) => {
      if (!resposta.ok) throw new Error("Perfil indisponível.");
      const dados = await resposta.json() as { id?: string; imagemPerfilUrl?: string | null };
      if (dados.id !== perfil.uid) throw new Error("Identidade do perfil divergente.");
      if (!atual()) return;
      perfil.foto = reutilizarAvatar(dados.imagemPerfilUrl?.trim() || null, perfil.uid);
      // Uma URL já expirada não pode produzir renovação contínua.
      perfil.pronta = !dados.imagemPerfilUrl || !!perfil.foto;
      avisarPerfil(perfil);
      agendarPerfil(perfil);
    })
    .catch(() => {
      if (!atual()) return;
      perfil.foto = null;
      perfil.pronta = false;
      avisarPerfil(perfil);
    })
    .finally(() => {
      if (!atual()) return;
      perfil.pendente = undefined;
      perfil.abortar = undefined;
    });
  return perfil.pendente;
}

function assinarPerfil(contexto: ContextoAvatar, ouvinte: (foto: string | null) => void) {
  if (!contextoPermitido(contexto)) { ouvinte(null); return () => {}; }
  const uid = contexto.id;
  if (!perfilAvatar || perfilAvatar.contexto !== contexto) {
    if (perfilAvatar) { pararPerfil(perfilAvatar); perfilAvatar.foto = null; avisarPerfil(perfilAvatar); }
    perfilAvatar = { contexto, uid, foto: null, pronta: false, bloqueado: false, ouvintes: new Set() };
  }
  const perfil = perfilAvatar;
  perfil.ouvintes.add(ouvinte);
  void carregarPerfil(perfil);
  return () => {
    perfil.ouvintes.delete(ouvinte);
    if (!perfil.ouvintes.size) {
      clearTimeout(perfil.timer);
      perfil.timer = undefined;
      // O GET já compartilhado pode concluir para uma remontagem; logout/troca cancela.
    }
  };
}

function invalidarAvatares(logout = false) {
  if (logout) {
    sessaoAvatarBloqueada = true;
    if (contextoAvatar) contextosRevogados.add(contextoAvatar);
    if (perfilAvatar) contextosRevogados.add(perfilAvatar.contexto);
  }
  urlsAvatar.clear();
  revisaoAvatar += 1;
  varianteAvatar += 1;
  const perfil = perfilAvatar;
  if (perfil) {
    pararPerfil(perfil);
    perfil.pronta = false;
    perfil.foto = null;
    perfil.bloqueado = sessaoAvatarBloqueada;
    avisarPerfil(perfil);
    if (!sessaoAvatarBloqueada) queueMicrotask(() => {
      if (perfilAvatar === perfil && perfil.ouvintes.size) void carregarPerfil(perfil);
    });
  }
  for (const ouvinte of ouvintesAvatar) ouvinte();
}

observarInvalidacaoCacheCliente((prefixo) => {
  if (!prefixo || "/api/perfil".includes(prefixo)) invalidarAvatares(!prefixo);
});

function fotoAtualizada() {
  // O salvamento já invalida /api/perfil antes de emitir o evento existente.
  if (!sessaoAvatarBloqueada && !perfilAvatar?.pendente) invalidarAvatares();
}
function storageAtualizado(event: StorageEvent) {
  if (!sessaoAvatarBloqueada && (event.key === null || event.key.startsWith("bp-foto-perfil:"))) invalidarAvatares();
}
function assinarAvatares(ouvinte: () => void) {
  if (!ouvintesAvatar.size) {
    window.addEventListener("bp:foto-perfil-atualizada", fotoAtualizada);
    window.addEventListener("storage", storageAtualizado);
  }
  ouvintesAvatar.add(ouvinte);
  return () => {
    ouvintesAvatar.delete(ouvinte);
    if (!ouvintesAvatar.size) {
      window.removeEventListener("bp:foto-perfil-atualizada", fotoAtualizada);
      window.removeEventListener("storage", storageAtualizado);
    }
  };
}

export function AvatarComFoto({
  src,
  alt,
  className,
  children,
  tone,
  style,
}: {
  src?: string | null;
  alt: string;
  className: string;
  children: ReactNode;
  tone?: number | string;
  style?: CSSProperties;
}) {
  const contexto = useExperienciaProduto().dados;
  const uid = contexto?.id ?? null;
  const [revisao, setRevisao] = useState(revisaoAvatar);
  const [falhou, setFalhou] = useState<string | null>(null);
  const fonte = src?.trim() || null;
  const [resolvida, setResolvida] = useState<{ fonte: string | null; contexto: ContextoAvatar | null; foto: string | null; revisao: number } | null>(null);

  useEffect(() => assinarAvatares(() => setRevisao(revisaoAvatar)), []);
  useEffect(() => {
    habilitarContextoAvatar(contexto);
    setResolvida({ fonte, contexto, foto: reutilizarAvatar(fonte, uid), revisao });
    setFalhou(null);
  }, [fonte, contexto, uid, revisao]);
  const foto = (!contexto || contextoPermitido(contexto)) && resolvida?.fonte === fonte
    && resolvida?.contexto === contexto && resolvida?.revisao === revisao
    ? resolvida.foto : null;

  if (foto && falhou !== foto) {
    return (
      <div className={`${className} overflow-hidden`} data-avatar-tone={tone} style={style}>
        <img key={revisao} src={foto} alt={alt} className="h-full w-full object-cover" onError={() => {
          const entrada = uid && identificarAvatar(foto, uid);
          if (entrada) urlsAvatar.delete(entrada.chave);
          if (perfilAvatar?.uid === uid && perfilAvatar.foto === foto) {
            pararPerfil(perfilAvatar);
            perfilAvatar.pronta = false;
            perfilAvatar.foto = null;
          }
          setFalhou(foto);
        }} />
      </div>
    );
  }

  return (
    <div className={className} data-avatar-tone={tone} style={style}>
      {children}
    </div>
  );
}

export function FotoPerfilUsuario({
  className,
  children,
  tone,
  style,
}: {
  className: string;
  children: ReactNode;
  tone?: number | string;
  style?: CSSProperties;
}) {
  const contexto = useExperienciaProduto().dados;
  const [perfil, setPerfil] = useState<{ contexto: ContextoAvatar; foto: string | null } | null>(null);

  useEffect(() => {
    habilitarContextoAvatar(contexto);
    if (!contexto) return;
    return assinarPerfil(contexto, (foto) => setPerfil({ contexto, foto }));
  }, [contexto]);
  const foto = contextoPermitido(contexto) && perfil?.contexto === contexto ? perfil.foto : null;

  return (
    <AvatarComFoto src={foto} alt="Foto de perfil" className={className} tone={tone} style={style}>
      {children}
    </AvatarComFoto>
  );
}
